import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { get } from 'lodash';
import {
  Paper,
  Button,
  Popover,
  Badge,
  ToggleButton
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faComment } from '@fortawesome/free-solid-svg-icons';
import { faMessages } from '@fortawesome/pro-regular-svg-icons';
import { useDataQuery, useDataMutation } from 'hooks';
import { setMessageModalJobId } from 'actions';
import { Box } from 'components/common_v2/layouts';
import { BodyLarge } from 'components/common_v2/typography';
import getText from 'context/language/getText';
import { urlMap } from 'routes/urlMap';
import DataContainer from 'containers/data';
import InlineLoader from 'components/common/loaders/InlineLoader';
import { getUnreadMessages, markMessagesReadByJobs } from './graphql';
import { MessageList } from './components';
import { EmptyState } from '../emptyState';

export const MessagesContent = ({ messageInfo, reexecuteQuery }) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const history = useHistory();
  const messages = get(messageInfo, 'unreadMessages', []);
  const unreadMessagesCount = get(messageInfo, 'totalUnreadMessagesCount', 0);
  const [popoverAnchorEl, setPopoverAnchorEl] = useState(null);
  const [_markMessagesAsReadRes, executeMarkMessagesReadByJobs] = useDataMutation(markMessagesReadByJobs);

  const handlePopoverClick = (event) => {
    setPopoverAnchorEl(event.currentTarget);
  };
  const handleMessageClick = (jobId) => {
    dispatch(setMessageModalJobId(jobId));
    executeMarkMessagesReadByJobs({ includedJobs: [jobId] }).then(() => {
      reexecuteQuery.messageInfo({ requestPolicy: 'network-only' });
      setPopoverAnchorEl(null);
      history.push(urlMap.MESSAGES);
    });
  };

  const handleMarkAllAsRead = () => {
    executeMarkMessagesReadByJobs({ includedJobs: messages.map((message) => message.job.id) }).then(() => {
      reexecuteQuery.messageInfo({ requestPolicy: 'network-only' });
    });
  };

  const handleViewAll = () => {
    setPopoverAnchorEl(null);
    history.push(urlMap.MESSAGES);
  };

  return (
    <>
      <ToggleButton
        size="small"
        sx={{ border: 'none', borderRadius: '50%' }}
        onClick={handlePopoverClick}
        selected={Boolean(popoverAnchorEl)}
        data-test-id="notifications__message-toggle"
      >
        {
          unreadMessagesCount > 0 ? (
            <Badge
              badgeContent={unreadMessagesCount}
              slotProps={{
                badge: {
                  sx: { color: 'notification.contrast', backgroundColor: 'notification.main' }
                }
              }}
            >
              <FontAwesomeIcon icon={faComment} color="black" size="xs" style={{ height: '16px' }} />
            </Badge>
          ) : (
            <FontAwesomeIcon icon={faComment} color="black" size="xs" style={{ height: '16px' }} />
          )
        }
      </ToggleButton>
      <Popover
        open={Boolean(popoverAnchorEl)}
        anchorEl={popoverAnchorEl}
        onClose={() => setPopoverAnchorEl(null)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
      >
        <Paper sx={{ width: '420px' }} data-test-id="notifications__message-container">
          <Box
            alignItems="center"
            justifyContent="space-between"
            padding="8px"
            style={{ borderBottom: `1px solid ${theme.palette.action.divider}` }}
          >
            <BodyLarge className="bold">{getText('header.messages.header.title')}</BodyLarge>
            <Button
              variant="text"
              color="inherit"
              onClick={handleMarkAllAsRead}
              data-test-id="notifications__messages-mark-all-as-read"
            >
              {getText('header.notifications.buttons.markAllAsRead')}
            </Button>
          </Box>
          <div style={{ padding: '8px' }}>
            {
              unreadMessagesCount > 0 ? (
                <MessageList
                  messages={messages}
                  handleMessageClick={handleMessageClick}
                />
              ) : (
                <EmptyState
                  title={getText('header.messages.emptyState.title')}
                  description={getText('header.messages.emptyState.description')}
                  icon={faMessages}
                />
              )
            }
          </div>
          <div>
            <Button style={{ width: '100%' }} color="inherit" onClick={handleViewAll}>
              {getText('header.messages.footer.viewAll')}
            </Button>
          </div>
        </Paper>
      </Popover>
    </>
  );
};

MessagesContent.propTypes = {
  messageInfo: PropTypes.shape({
    unreadMessages: PropTypes.array.isRequired,
    totalUnreadMessagesCount: PropTypes.number.isRequired
  }).isRequired,
  reexecuteQuery: PropTypes.shape({
    messageInfo: PropTypes.func.isRequired
  }).isRequired
};

export const Messages = () => (
  <DataContainer
    dataMap={{
      messageInfo: useDataQuery(getUnreadMessages),
    }}
    displayComponent={MessagesContent}
    loaderComponent={InlineLoader}
    errorComponent={() => null}
  />
);
